import { type Breakpoint, Stack, type StackProps, type Theme, styled } from "@mui/material";
import type { AtLeastOne } from "@root/utils/typeHelper";
import type React from "react";

export const getStackGridWidthCalc = (theme: Theme, perLine: number, spacing: number, offset?: number) => {
  return `calc(${100 / perLine}% - ${theme.spacing((spacing * (perLine - 1)) / perLine)} ${offset ? `- (${theme.spacing(spacing)} + ${offset}px) / ${perLine}` : ""})`;
};

type TPerline = AtLeastOne<Record<Breakpoint, number>>;

const buildPerlineBreakpoints = (perLine: TPerline | number, theme: Theme, nbrColumnGap: number, strict?: boolean) => {
  const bp = theme.breakpoints.keys.reduce((globalStyles, breakpoint, currIndex) => {
    let numberOfLine = 0;

    if (currIndex === 0) {
      numberOfLine = 1;
    }

    if (perLine[breakpoint]) {
      numberOfLine = perLine[breakpoint];
    }

    if (typeof perLine === "number") {
      numberOfLine = perLine;
    }

    globalStyles[theme.breakpoints.up(breakpoint)] = {
      "& > *": {
        flex: `0 0 ${getStackGridWidthCalc(theme, numberOfLine, nbrColumnGap)}`,
        ...(strict && { maxWidth: getStackGridWidthCalc(theme, numberOfLine, nbrColumnGap) }),
      },
    };

    return globalStyles;
  }, {});
  return bp;
};

const PREFIX = "StackGrid";
export const stackGridClasses = {
  fullWidth: `${PREFIX}-fullWidth`,
  forcedFullWidth: `${PREFIX}-forcedFullWidth`,
};

export interface IStackGridProps extends Omit<StackProps, "spacing"> {
  perLine?: TPerline | number;
  strict?: boolean;
}

export const StackGrid = styled<React.FC<IStackGridProps>>(Stack, {
  shouldForwardProp: (propName) => propName !== "perLine" && propName !== "strict",
})(({ theme, strict, columnGap, gap, perLine = 1 }) => {
  const nbrColumnGap = Number(columnGap ?? gap) || 0;
  return {
    flexDirection: "row",
    flexWrap: "wrap",
    ...buildPerlineBreakpoints(perLine, theme, nbrColumnGap, strict),
    [`& .${stackGridClasses.fullWidth}`]: {
      maxWidth: "100%",
      flexBasis: "auto",
    },
    [`& .${stackGridClasses.forcedFullWidth}`]: {
      minWidth: "100%",
    },
  };
});
